import React, { useState } from 'react';

/**
 * 🧰 The Shelf — every /build lives here forever (slash-commands spec §3a).
 *
 * 08-04 PLANK REDESIGN (Nina picked the mock: cozy clutter + always-on
 * plaques + bottom sheet on phones): items STAND on wood planks as big art
 * instead of stacking as list rows. Each item wears a deterministic tilt and
 * size wobble (seeded off its handle, so the shelf never reshuffles between
 * renders), a name plaque, and the newest build gets the ✨. Tapping an item
 * picks it up and opens a card: name, copyable #handle, provenance, and the
 * verbs — Gift and Combine seed the composer via the parent's callbacks
 * (onGift/onCombine absent = chips hidden, e.g. read-only contexts).
 *
 * Chrome stays in the page (drawer on desktop, bottom sheet on phones,
 * Locals-page section) — this component is just planks + card.
 */

const WOOD_TEXT = '#3d2b12';
const WOOD_FAINT = 'rgba(61,43,18,0.8)';
const CREAM = '#fff8e6';

const shelfAge = (iso) => {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 0) return null;
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
};

// Deterministic per-item wobble: hash the handle so a given item always
// leans the same way — Math.random() here would reshuffle the shelf on
// every poll-driven re-render.
const wobble = (handle) => {
  let h = 0;
  const s = String(handle || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return {
    rot: ((h % 13) - 6),               // -6..6 deg
    scale: 0.9 + ((h >> 4) % 23) / 100, // 0.9..1.12
  };
};

// 🎁 F2 (Nina 09-26: "Kenley left you something"): a ported room's welcome
// gift is the RESIDENT's (via_reward 'resident_gift', gift_for = the owner's
// name). The owner reads "left you something"; anyone else reads who it was
// for, by first name. Every other provenance reads exactly as before.
export const provLine = (item, age, { isOwner = false } = {}) => {
  const by = item.built_by || 'someone';
  if (item.via_reward === 'resident_gift') {
    const ageTail = age ? ` · ${age}` : '';
    if (isOwner) return `🎁 ${by} left you something${ageTail}`;
    const forWho = String(item.gift_for || '').trim().split(/\s+/)[0];
    return forWho ? `🎁 ${by} left this for ${forWho}${ageTail}` : `🎁 a gift from ${by}${ageTail}`;
  }
  const verb = item.via_reward
    ? (String(item.via_reward).includes('quest') ? '🎁 gifted by' : '🎁 dropped by')
    : item.via_toy ? '🧸 tinkered by'
    : item.via_code ? '💻 coded by'
    : 'built by';
  return `${verb} ${by}${age ? ` · ${age}` : ''}`;
};

function ShelfItem({ item, fresh, picked, onPick }) {
  const handle = item.handle || '';
  const name = item.name || `#${handle}`;
  const { rot, scale } = wobble(handle);
  const lift = picked ? ' translateY(-6px)' : '';
  const liftScale = picked ? scale * 1.18 : scale;
  return (
    <button
      onClick={onPick}
      aria-label={name}
      aria-expanded={picked}
      // data-prop-ui: shields the tile from generated-theme blanket button
      // backgrounds (ThemeStyleInjector) — the emoji must stand bare on wood.
      data-prop-ui=""
      style={{
        background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer',
        position: 'relative', textAlign: 'center', WebkitTapHighlightColor: 'transparent',
        minWidth: 0,
        // 🪵 10-02 shelf (Nina's screenshot): a generated theme's blanket `button { text-shadow }` (data-prop-ui
        // shields the background only) smudged the 8.5px plaque this button holds; inline beats the theme rule
        textShadow: 'none',
      }}
    >
      {/* Icon-first (08-05): new builds mint a square house-cartoon sticker
          (icon_url) that's MADE for this size — it wins the plank. Emoji-first
          otherwise (Nina 08-04: the 16:9 scene art read as a gray tile at
          46px; it lives in the pickup card). Scene-thumb only remains for
          legacy items with art but no emoji (backfill script retires those). */}
      {item.icon_url ? (
        <img
          src={item.icon_url}
          alt=""
          style={{
            width: 50, height: 50, objectFit: 'contain',
            display: 'block', margin: '0 auto',
            // Icons arrive background-KEYED (server flood-fill → real alpha,
            // Nina 08-05: no more squares, white OR black) — so no border,
            // no tile, no blend tricks: the object just stands on the plank
            // like the emoji do, and drop-shadow follows the cutout shape.
            transform: `rotate(${picked ? 0 : rot}deg) scale(${liftScale})${lift}`,
            transformOrigin: '50% 90%',
            transition: 'transform 0.18s ease',
            filter: fresh
              ? 'drop-shadow(0 5px 4px rgba(0,0,0,0.3)) drop-shadow(0 0 8px rgba(255,215,110,0.8))'
              : 'drop-shadow(0 5px 4px rgba(0,0,0,0.3))',
          }}
        />
      ) : (item.image_url && !item.emoji) ? (
        /* Legacy pre-icon items: un-keyed 16:9 scene thumb keeps the framed
           tile look (backfill retires these). */
        <img
          src={item.image_url}
          alt=""
          style={{
            width: 46, height: 46, borderRadius: 8, objectFit: 'cover',
            display: 'block', margin: '0 auto',
            border: '1px solid #b28f4a',
            transform: `rotate(${picked ? 0 : rot}deg) scale(${liftScale})${lift}`,
            transformOrigin: '50% 90%',
            transition: 'transform 0.18s ease',
            boxShadow: '0 6px 6px rgba(0,0,0,0.3)',
          }}
        />
      ) : (
        <span style={{
          display: 'block', fontSize: 38, lineHeight: 1,
          transform: `rotate(${picked ? 0 : rot}deg) scale(${liftScale})${lift}`,
          transformOrigin: '50% 90%',
          transition: 'transform 0.18s ease',
          filter: fresh
            ? 'drop-shadow(0 5px 4px rgba(0,0,0,0.3)) drop-shadow(0 0 8px rgba(255,215,110,0.8))'
            : 'drop-shadow(0 5px 4px rgba(0,0,0,0.3))',
        }}>{item.emoji || '📦'}</span>
      )}
      {fresh && (
        <span aria-hidden="true" style={{ position: 'absolute', top: -8, right: -2, fontSize: 12 }}>✨</span>
      )}
      {/* Museum plaque (Nina's pick: always visible) */}
      <span style={{
        display: 'block', margin: '6px auto 0', maxWidth: 78,
        fontSize: 8.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase',
        color: CREAM, background: 'rgba(61,43,18,0.75)',
        borderRadius: 4, padding: '1px 5px',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{name}</span>
    </button>
  );
}

function ItemCard({ item, onGift, onCombine, onClose, copied, onCopy, isOwner = false }) {
  const handle = item.handle || '';
  const name = item.name || `#${handle}`;
  const age = shelfAge(item.built_at);
  return (
    <div style={{
      margin: '4px 2px 10px', borderRadius: 12, padding: '10px 12px', textShadow: 'none',
      background: 'rgba(255,248,230,0.94)', border: '1px solid #c9a45e',
      boxShadow: '0 4px 12px rgba(0,0,0,0.28)',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {item.icon_url ? (
        /* keyed alpha — sits clean on the cream card, shadow hugs the cutout */
        <img src={item.icon_url} alt="" style={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0, filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.25))' }} />
      ) : item.emoji || !item.image_url ? (
        <span style={{ fontSize: 40, lineHeight: 1, filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.25))' }}>{item.emoji || '📦'}</span>
      ) : (
        <img src={item.image_url} alt="" style={{ width: 46, height: 46, borderRadius: 8, objectFit: 'cover', border: '1px solid #b28f4a', flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, fontSize: '0.92em', color: WOOD_TEXT }}>{name}</span>
          <button
            onClick={() => onCopy(handle)}
            title={`Copy #${handle} for combining`}
            data-prop-ui=""
            style={{
              fontSize: '0.66em', fontWeight: 800, border: 'none', cursor: 'pointer',
              padding: '2px 7px', borderRadius: 6,
              ...(copied === handle
                ? { color: '#fff', background: '#1c7c2e', '--prop-bg': '#1c7c2e' }
                : { color: WOOD_TEXT, background: 'rgba(178,143,74,0.35)', '--prop-bg': 'rgba(178,143,74,0.35)' }),
            }}
          >{copied === handle ? 'copied!' : `#${handle}`}</button>
        </div>
        {item.via_combo && (
          <div style={{ fontSize: '0.72em', color: WOOD_FAINT, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ⚗️ #{item.via_combo[0]} + #{item.via_combo[1]}
          </div>
        )}
        <div style={{ fontSize: '0.72em', color: WOOD_FAINT, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {provLine(item, age, { isOwner })}
        </div>
        {(onGift || onCombine) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {onGift && (
              <button
                onClick={() => onGift(handle)}
                data-prop-ui=""
                style={{
                  fontSize: '0.72em', fontWeight: 800, cursor: 'pointer',
                  border: '1px solid #7a5227', borderRadius: 999,
                  padding: '4px 12px', background: WOOD_TEXT, color: CREAM,
                  '--prop-bg': WOOD_TEXT,
                }}
              >🎁 Gift</button>
            )}
            {onCombine && (
              <button
                onClick={() => onCombine(handle)}
                data-prop-ui=""
                style={{
                  fontSize: '0.72em', fontWeight: 800, cursor: 'pointer',
                  border: '1px solid #7a5227', borderRadius: 999,
                  padding: '4px 12px', background: 'transparent', color: WOOD_TEXT,
                }}
              >⚗️ Combine</button>
            )}
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        aria-label="Put the item back"
        data-prop-ui=""
        style={{ background: 'none', border: 'none', color: WOOD_TEXT, fontWeight: 800, cursor: 'pointer', padding: '0 2px', fontSize: '0.9em' }}
      >✕</button>
      </div>
      {/* The build's scene art lives HERE now (08-04) — full-width where it
          reads as a picture, not squeezed into a gray shelf thumb. */}
      {item.image_url && (
        <img
          src={item.image_url}
          alt=""
          loading="lazy"
          style={{
            display: 'block', width: '100%', aspectRatio: '16/9', objectFit: 'cover',
            borderRadius: 8, border: '1px solid #b28f4a', marginTop: 8,
          }}
        />
      )}
    </div>
  );
}

// isOwner: the viewer is the ported room's owner (the page's isPortedOwner);
// it only changes how a resident's gift reads (provLine).
export default function SpaceShelf({ items, onGift, onCombine, perPlank = 3, isOwner = false }) {
  const [copied, setCopied] = useState(null);
  const [pickedId, setPickedId] = useState(null);

  const copyHandle = (handle) => {
    if (!handle) return;
    try { navigator.clipboard?.writeText(`#${handle}`); } catch (e) { /* clipboard blocked: the flash still teaches the handle */ }
    setCopied(handle);
    setTimeout(() => setCopied(prev => (prev === handle ? null : prev)), 1500);
  };

  if (!items?.length) {
    return (
      <div style={{ padding: '18px 12px 22px', textAlign: 'center', color: WOOD_TEXT }}>
        <div style={{ fontSize: '2em', marginBottom: 6 }}>🪵</div>
        <div style={{ fontSize: '0.9em', fontWeight: 700 }}>Nothing on the shelf yet!</div>
        <div style={{ fontSize: '0.78em', marginTop: 6, color: WOOD_FAINT }}>
          Type <b>/build</b> to make something for the space.
          <br />Then combine: <b>/build #stick + #stone</b>
        </div>
      </div>
    );
  }

  const newestFirst = [...items].reverse();
  const itemKey = (item, i) => item.id || `${item.handle}-${i}`;
  // ✨ only while the newest build is actually new — a week-old shelf
  // shouldn't sparkle forever.
  const freshKey = (() => {
    const it = newestFirst[0];
    if (!it || !it.built_at) return null;
    const ageMs = Date.now() - new Date(it.built_at).getTime();
    return (Number.isFinite(ageMs) && ageMs >= 0 && ageMs < 24 * 3600 * 1000) ? itemKey(it, 0) : null;
  })();

  const planks = [];
  for (let i = 0; i < newestFirst.length; i += perPlank) planks.push(newestFirst.slice(i, i + perPlank));

  const pickedItem = newestFirst.find((it, i) => itemKey(it, i) === pickedId) || null;

  return (
    <div style={{ padding: '2px 2px 8px' }}>
      {planks.map((group, pi) => {
        const plankHasPicked = pickedItem && group.some((it) => itemKey(it, newestFirst.indexOf(it)) === pickedId);
        return (
          <div key={pi} style={{ marginBottom: 4 }}>
            <div style={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around',
              padding: '16px 4px 0', minHeight: 80,
            }}>
              {group.map((it) => {
                const gi = newestFirst.indexOf(it);
                const k = itemKey(it, gi);
                return (
                  <ShelfItem
                    key={k}
                    item={it}
                    fresh={k === freshKey}
                    picked={k === pickedId}
                    onPick={() => setPickedId(pickedId === k ? null : k)}
                  />
                );
              })}
            </div>
            {/* The plank itself: lit top edge, shadowed front face */}
            <div style={{
              height: 13, borderRadius: 3,
              background: 'linear-gradient(180deg, #e2b87c 0%, #b98d54 30%, #8a5f33 70%, #6f4a24 100%)',
              border: '1px solid #7a5227',
              boxShadow: '0 5px 7px rgba(0,0,0,0.28)',
            }} />
            {/* Card opens right under the plank you tapped */}
            {plankHasPicked && (
              <ItemCard
                item={pickedItem}
                onGift={onGift}
                onCombine={onCombine}
                onClose={() => setPickedId(null)}
                copied={copied}
                onCopy={copyHandle}
                isOwner={isOwner}
              />
            )}
          </div>
        );
      })}
      <div style={{ fontSize: '0.7em', textAlign: 'center', color: WOOD_FAINT, marginTop: 4 }}>
        tap an item to pick it up
      </div>
    </div>
  );
}
